import type { Enrollment } from '../types/GrooverTypes'
import { PaymentMethod, PaymentSource } from '../types/GrooverTypes'

/**
 * Total öre refunded toward a reception walk-in payment (Swish QR or Stripe Terminal card) identified
 * by `payment.paymentRef` on the enrollment (Swish: `instructionUUID`; Stripe: PaymentIntent id).
 *
 * Swish refunds correlate via `swishPaymentReference` (the payer-visible ref differs from the
 * `payeePaymentReference`/instructionUUID used as `paymentRef`). Stripe refunds correlate directly via
 * `originalPaymentReference === paymentRef` — both are the same PaymentIntent id — so no indirection
 * is needed there.
 */
export function sumWalkInRefundedCents(enrollment: Enrollment | undefined | null, walkInPaymentRef: string): number {
  if (!enrollment?.payments?.length) return 0
  const pay = enrollment.payments.find((p) => p.type === 'pay' && p.paymentRef === walkInPaymentRef)
  if (!pay) return 0

  const isSwish = String(pay.source).trim().toLowerCase() === String(PaymentSource.SwishWalkIn).toLowerCase()
  const isStripe = String(pay.source).trim().toLowerCase() === String(PaymentSource.StripeWalkIn).toLowerCase()
  if (!isSwish && !isStripe) return 0

  const correlationKey = isSwish ? pay.swishPaymentReference : pay.paymentRef
  if (!correlationKey) return 0

  const refunds = enrollment.payments.filter(
    (r) =>
      r.type === 'refund' &&
      r.originalPaymentReference === correlationKey &&
      (isSwish ? r.paymentMethod === PaymentMethod.Swish : true)
  )
  return Math.abs(refunds.reduce((sum, r) => sum + (r.totalAmount ?? 0), 0))
}
